import React, { useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useMatch } from 'react-router';
import {
  Stack,
  Alert,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import Papa from 'papaparse';
import { getNormalized } from '../../redux/actions/rtemFiles';
import TooltipTab from '../util/TooltipTab';
import DataPreview from './analyze/DataPreview';
import NumericalSummary from './analyze/NumericalSummary';
import VisualSummary from './analyze/VisualSummary';
import RawData from './analyze/RawData';

const Analyze = ({ normalize, normalizations }) => {
  const match = useMatch('/analyze/:path');
  const { path } = match?.params ?? '';
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState();
  const [data, setData] = useState();

  const normal = useMemo(() => {
    const normalization = normalizations?.[path];
    if (normalization) {
      return normalization;
    }
    const result = normalize(path);
    return { urls: result?.urls, error: result?.error };
  }, [normalizations, normalize, path]);

  useEffect(() => {
    if (normal?.stats) {
      fetch(normal?.stats)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw response;
        })
        .then((result) => {
          setStats(result);
        });
    }
  }, [normal?.stats]);

  useEffect(() => {
    if (data === undefined && normal?.data && (tab === 2 || tab === 3)) {
      fetch(normal?.data)
        .then((response) => {
          if (response.ok) {
            return response.text();
          }
          throw response;
        })
        .then((csv) => Papa.parse(csv, { header: true, dynamicTyping: true, skipEmptyLines: true }))
        .then((result) => {
          const newData = result.data.toSorted((a, b) => a.Timestamp - b.Timestamp);
          setData(newData);
        });
    }
  }, [normal?.data, data, setData, tab]);

  if (normal?.error) {
    return <Alert severity="error">{normal?.error}</Alert>;
  }

  if (!stats) {
    return (
      <Box sx={{ display: 'grid', justifyContent: 'center' }}>
        <CircularProgress size="150px" />
      </Box>
    );
  }

  // 1. request presigned urls to put in redux
  // 1b. if url not in redux, display spinner
  // 2. Once we get the url, we can fetch the contents and then remove spinner and present actual details

  return (
    <>
      <Typography align="center" variant="h6">
        {path}
      </Typography>
      <Stack justifyContent="center" direction="row">
        <Tabs
          sx={{ display: 'grid', justifyContent: 'center' }}
          textColor="secondary"
          indicatorColor="secondary"
          value={tab}
          onChange={(event, newValue) => setTab(newValue)}
        >
          <Tab label="Data Preview" />
          <Tab label="Numerical Summary" />
          <TooltipTab
            label="Raw Data"
            message={
              normal?.dataSize > 250000000
                ? 'Raw Data cannot be explored for files exceeding 250MB'
                : null
            }
          />
          <TooltipTab
            label="Visual Summary"
            message={
              normal?.dataSize > 250000000
                ? 'Visual Summary cannot be viewed for files exceeding 250MB'
                : null
            }
          />
          {
            // <Tab label="High Level Data Review" />
          }
        </Tabs>
        <IconButton
          component="a"
          href={normal?.data}
          target="_blank"
          rel="noopener noreferrer"
          size="large"
        >
          <Save />
        </IconButton>
      </Stack>

      {tab === 0 ? <DataPreview stats={stats} /> : null}
      {tab === 1 ? <NumericalSummary stats={stats} /> : null}
      {(tab === 2 || tab === 3) && !data ? (
        <Box sx={{ display: 'grid', justifyContent: 'center' }}>
          {' '}
          <CircularProgress size="150px" />{' '}
        </Box>
      ) : null}
      {tab === 2 && data ? <RawData data={data} /> : null}
      {tab === 3 && data ? <VisualSummary data={data} site={path.replace('/', ' - ')} /> : null}
    </>
  );
};

function mapStateToProps(state) {
  const {
    rtemFiles: { normalizations },
  } = state;
  return {
    normalizations,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    normalize: (path) => dispatch(getNormalized(path)),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(Analyze);
